/* Fog & Form — motion and instruments (design-system layer v3).
   Four small behaviours, each independent and each a no-op when its markup is absent:
     1. Hero headline: split on <br> into masked lines that rise into place on load.
     2. Premise: ink the statement in word by word as it is read down the page (--p).
     3. Catalog index: swap the plate to the collection under the pointer or focus.
     4. Conditions rail: Pacific-time clock plus the latest public NWS observation.
        With live fog on, the same observation sets how much fog the hero carries.
   Motion is skipped under prefers-reduced-motion and inside the theme editor.
   No dependencies, no layout thrash: one passive scroll listener, one rAF per tick. */
(function () {
  'use strict';

  var html = document.documentElement;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var design = html.classList.contains('shopify-design-mode');
  var motion = !reduce && !design;

  /* ---------------------------------------------------------------------- 1 */
  function splitHeadlines() {
    if (!motion) return;
    var blocks = document.querySelectorAll('.hero .text-block.h1');
    for (var b = 0; b < blocks.length; b++) {
      var block = blocks[b];
      var el = block.firstElementChild;
      if (!el || block.classList.contains('ff-split')) continue;
      // Only split plain text + <br>; anything richer (links, emphasis) is left alone.
      var ok = true;
      for (var c = 0; c < el.children.length; c++) {
        if (el.children[c].tagName !== 'BR') { ok = false; break; }
      }
      if (!ok) continue;
      var parts = el.innerHTML.split(/<br\s*\/?>/i);
      var out = '';
      for (var i = 0; i < parts.length; i++) {
        var t = parts[i].replace(/^\s+|\s+$/g, '');
        if (!t) continue;
        out += '<span class="ff-line"><span class="ff-line__in" style="--l:' + i + '">' + t + '</span></span> ';
      }
      if (!out) continue;
      el.innerHTML = out;
      block.classList.add('ff-split');
      var r = block.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        release(block);
      } else if ('IntersectionObserver' in window) {
        lineObserver().observe(block);
      } else {
        block.classList.add('ff-lines-in');
      }
    }
  }
  function release(block) {
    // two frames so the hidden state is committed before the transition starts
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { block.classList.add('ff-lines-in'); });
    });
  }
  var _lineIO;
  function lineObserver() {
    if (_lineIO) return _lineIO;
    _lineIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('ff-lines-in');
        _lineIO.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    return _lineIO;
  }

  /* ---------------------------------------------------------------------- 2 */
  var inks = [];
  var ticking = false;
  function collectInks() {
    inks = [];
    if (!motion) return;
    var nodes = document.querySelectorAll('[data-ff-ink]');
    for (var i = 0; i < nodes.length; i++) inks.push(nodes[i]);
    paintInks();
  }
  function paintInks() {
    ticking = false;
    var vh = window.innerHeight || 1;
    for (var i = 0; i < inks.length; i++) {
      var r = inks[i].getBoundingClientRect();
      // starts as the statement's top edge clears 88% of the viewport,
      // finishes when its bottom edge reaches 52%
      var start = vh * 0.88;
      var end = vh * 0.52 - r.height;
      var p = (start - r.top) / Math.max(1, start - end);
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      inks[i].style.setProperty('--p', p.toFixed(3));
    }
  }
  function onScroll() {
    if (ticking || !inks.length) return;
    ticking = true;
    requestAnimationFrame(paintInks);
  }

  /* ---------------------------------------------------------------------- 3 */
  function initIndexes() {
    var roots = document.querySelectorAll('[data-ff-index]');
    for (var i = 0; i < roots.length; i++) bindIndex(roots[i]);
  }
  function bindIndex(root) {
    if (root.__ffBound) return;
    root.__ffBound = true;
    var rows = root.querySelectorAll('[data-ff-index-row]');
    var imgs = root.querySelectorAll('[data-ff-index-img]');
    var cap = root.querySelector('[data-ff-index-cap]');
    if (!rows.length || !imgs.length) return;
    function show(idx) {
      var hit = null;
      for (var j = 0; j < imgs.length; j++) {
        if (imgs[j].getAttribute('data-ff-index-img') === String(idx)) hit = imgs[j];
      }
      if (!hit) return; // collection without an image: keep the current plate
      for (var k = 0; k < imgs.length; k++) imgs[k].classList.toggle('is-on', imgs[k] === hit);
      if (cap) cap.textContent = hit.getAttribute('data-title') || '';
    }
    for (var r = 0; r < rows.length; r++) {
      (function (row) {
        var idx = row.getAttribute('data-ff-index-row');
        row.addEventListener('pointerenter', function () { show(idx); });
        row.addEventListener('focus', function () { show(idx); });
      })(rows[r]);
    }
  }

  /* ---------------------------------------------------------------------- 4 */
  function initConditions() {
    var roots = document.querySelectorAll('[data-ff-conditions]');
    for (var i = 0; i < roots.length; i++) bindConditions(roots[i]);
  }
  function bindConditions(root) {
    if (root.__ffBound) return;
    root.__ffBound = true;

    var clock = root.querySelector('[data-ff-clock]');
    var tz = root.getAttribute('data-tz') || 'America/Los_Angeles';
    var fmt = null;
    try {
      fmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
    } catch (e) { fmt = null; }
    function tick() { if (fmt && clock) clock.textContent = fmt.format(new Date()); }
    if (fmt && clock) {
      tick();
      root.setAttribute('data-ff-on', '');
      setInterval(tick, 20000);
    }

    var station = root.getAttribute('data-station');
    if (!station || !window.fetch) return;
    var key = 'ff-wx-' + station;
    var cached = null;
    try { cached = JSON.parse(window.sessionStorage.getItem(key) || 'null'); } catch (e) { cached = null; }
    if (cached && cached.t && Date.now() - cached.t < 10 * 60 * 1000 && cached.d) {
      paintConditions(root, cached.d);
      return;
    }
    fetch('https://api.weather.gov/stations/' + encodeURIComponent(station) + '/observations/latest', {
      headers: { Accept: 'application/geo+json' }
    })
      .then(function (res) { if (!res.ok) throw new Error('nws ' + res.status); return res.json(); })
      .then(function (json) {
        var p = (json && json.properties) || {};
        var d = {
          temp: p.temperature ? p.temperature.value : null,
          vis: p.visibility ? p.visibility.value : null,
          text: p.textDescription || '',
          ts: p.timestamp || null
        };
        try { window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: d })); } catch (e) { /* private mode */ }
        paintConditions(root, d);
      })
      .catch(function () { /* the rail is complete without weather */ });
  }
  function paintConditions(root, d) {
    // Ignore observations older than three hours: stale weather is worse than none.
    if (d.ts) {
      var age = Date.now() - new Date(d.ts).getTime();
      if (isFinite(age) && age > 3 * 60 * 60 * 1000) return;
    }
    function reveal(name, sel, text) {
      var cell = root.querySelector('[data-ff-live="' + name + '"]');
      var out = root.querySelector(sel);
      if (!cell || !out) return;
      out.textContent = text;
      cell.hidden = false;
    }
    if (typeof d.temp === 'number') {
      reveal('temp', '[data-ff-temp]', Math.round(d.temp * 9 / 5 + 32) + '°F');
    }
    var state = null;
    var text = String(d.text || '');
    if (typeof d.vis === 'number') {
      var mi = d.vis / 1609.344;
      reveal('vis', '[data-ff-vis]', (mi >= 9.95 ? '10+' : (mi < 1 ? mi.toFixed(2).replace(/^0/, '') : mi.toFixed(1))) + ' mi');
      if (d.vis < 1600) state = 'in';
      else if (d.vis < 8000) state = 'low';
    }
    if (/fog|mist/i.test(text)) state = 'in';
    else if (!state && /overcast|mostly cloudy|haze|drizzle|rain/i.test(text)) state = 'low';
    if (!state && (text || typeof d.vis === 'number')) state = 'out';
    if (state) {
      reveal('fog', '[data-ff-fogstate]', root.getAttribute('data-fog-' + state) || state);
      root.setAttribute('data-ff-fog-state', state);
      if (root.getAttribute('data-live-fog') === 'true') {
        // Fog amount, 0 (clear) to 1 (socked in). Visibility is read on a log
        // scale: ten miles or more is 0, half a mile or less is 1. Reported fog,
        // mist or low cloud sets a floor, so the hero never looks clearer than
        // the sky. The three density values are written inline and override the
        // density class chosen on the hero.
        var amt = 0;
        if (typeof d.vis === 'number' && d.vis > 0) {
          amt = Math.log(16000 / d.vis) / Math.log(20);
          amt = Math.max(0, Math.min(1, amt));
        }
        if (state === 'in') amt = Math.max(amt, 0.8);
        else if (state === 'low') amt = Math.max(amt, 0.4);
        var fogs = document.querySelectorAll('.ff-fog');
        for (var i = 0; i < fogs.length; i++) {
          fogs[i].classList.add('ff-fog--live');
          fogs[i].style.setProperty('--ff-fog-opacity', (0.5 + 0.46 * amt).toFixed(3));
          fogs[i].style.setProperty('--ff-fog-reach', (52 + 36 * amt).toFixed(1) + '%');
          fogs[i].style.setProperty('--ff-fog-solid', (8 + 24 * amt).toFixed(1) + '%');
        }
        root.setAttribute('data-ff-fog-amount', amt.toFixed(2));
      }
    }
  }

  /* -------------------------------------------------------------------- boot */
  function init() {
    splitHeadlines();
    collectInks();
    initIndexes();
    initConditions();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  // Theme editor: sections re-render in place
  document.addEventListener('shopify:section:load', function () {
    collectInks();
    initIndexes();
    initConditions();
  });
})();
